'use client'

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import {
  Close as CloseIcon,
  ContentCopy as CopyIcon,
  HistoryToggleOff as EmptyIcon,
  LocalShippingOutlined as PurchaseIcon,
  Refresh as RefreshIcon,
  SouthWest as InIcon,
  NorthEast as OutIcon,
  CloudDoneOutlined as ItsStockIcon,
  VerifiedOutlined as ItsIcon,
  Timeline as TimelineIcon,
  OpenInNewOutlined as KaynakIcon,
} from '@mui/icons-material'
import { useRouter } from 'next/navigation'
import { outfit } from '@/lib/fonts'
import CopyableBarkod from '@/components/common/CopyableBarkod'
import { faturaAdresi, islemKaynaklari, type HareketKaynagi } from '@/lib/urunKarti/hareketKaynagi'
import {
  gunlereBol,
  saatMetni,
  saatSaniyeMetni,
  tarihSaatMetni,
  type KarekodGecmisiYaniti,
  type KarekodZamanCizelgesiSatiri,
} from './gecmisTipleri'
import { formatIsoDate } from './types'

const fontFamily = outfit.style.fontFamily

interface KarekodGecmisiDialogProps {
  open: boolean
  seriNo: string | null
  gtin?: string | null
  /** Sunucuda kayıt yoksa başlıkta gösterilecek yedek ürün adı. */
  urunAdiYedek?: string | null
  onClose: () => void
}

export default function KarekodGecmisiDialog({
  open,
  seriNo,
  gtin,
  urunAdiYedek,
  onClose,
}: KarekodGecmisiDialogProps) {
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'
  const accent = isDark ? '#34ebc4' : '#00d4aa'

  const [yukleniyor, setYukleniyor] = useState(false)
  const [hata, setHata] = useState<string | null>(null)
  const [veri, setVeri] = useState<KarekodGecmisiYaniti | null>(null)
  const [kopyalandi, setKopyalandi] = useState(false)

  const router = useRouter()

  /** Çizelgedeki olayın kaynağına gider; pencere kapanır çünkü hedef başka bir sayfada açılır. */
  const kaynagaGit = useCallback(
    (kaynak: HareketKaynagi, yeniSekme: boolean) => {
      if (!kaynak.adres) return

      if (yeniSekme) {
        window.open(kaynak.adres, '_blank', 'noopener,noreferrer')
        return
      }
      onClose()
      router.push(kaynak.adres)
    },
    [router, onClose],
  )

  const getir = useCallback(async () => {
    if (!seriNo) return

    setYukleniyor(true)
    setHata(null)

    try {
      const adres = new URLSearchParams({ seriNo })
      if (gtin) adres.set('gtin', gtin)

      const headers: Record<string, string> = {}
      const tenantId = typeof window !== 'undefined' ? localStorage.getItem('tenantId') : null
      if (tenantId) headers['x-tenant-id'] = tenantId

      const yanit = await fetch(`/api/opas/tenant/karekod-gecmisi?${adres.toString()}`, { headers })
      const govde = (await yanit.json().catch(() => ({}))) as KarekodGecmisiYaniti & { error?: string }

      if (!yanit.ok) throw new Error(govde.error ?? 'Kutunun geçmişi getirilemedi.')

      setVeri(govde)
    } catch (sebep) {
      setHata(sebep instanceof Error ? sebep.message : 'Kutunun geçmişi getirilemedi.')
      setVeri(null)
    } finally {
      setYukleniyor(false)
    }
  }, [seriNo, gtin])

  useEffect(() => {
    if (open && seriNo) void getir()
  }, [open, seriNo, getir])

  useEffect(() => {
    if (!kopyalandi) return
    const zamanlayici = window.setTimeout(() => setKopyalandi(false), 1600)
    return () => window.clearTimeout(zamanlayici)
  }, [kopyalandi])

  const gunler = useMemo(() => gunlereBol(veri?.olaylar ?? []), [veri])
  const kutu = veri?.kutu ?? null
  const urunAdi = kutu?.urunAdi ?? urunAdiYedek ?? 'Karekod geçmişi'

  const seriKopyala = async () => {
    if (!seriNo) return
    try {
      await navigator.clipboard.writeText(seriNo)
      setKopyalandi(true)
    } catch {
      /* pano kapalıysa sessiz geçilir; kullanıcı seri numarasını zaten ekranda görüyor */
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      slotProps={{ backdrop: { sx: { backdropFilter: 'blur(4px)' } } }}
      PaperProps={{
        elevation: 0,
        sx: {
          fontFamily,
          borderRadius: 3,
          overflow: 'hidden',
          border: `1px solid ${alpha(accent, 0.35)}`,
          bgcolor: isDark ? '#161616' : '#ffffff',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'stretch', borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Box sx={{ width: 3, bgcolor: accent, flexShrink: 0 }} />
        <Stack direction="row" alignItems="center" gap={1} sx={{ flex: 1, px: 2, py: 1.25, minWidth: 0 }}>
          <TimelineIcon sx={{ color: accent, fontSize: 22 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography noWrap sx={{ fontFamily, fontWeight: 700, fontSize: '0.95rem', lineHeight: 1.25 }}>
              {urunAdi}
            </Typography>
            <Stack direction="row" alignItems="center" gap={0.5} sx={{ minWidth: 0 }}>
              <Typography
                noWrap
                sx={{ fontFamily: 'monospace', fontSize: '0.72rem', color: 'text.secondary' }}
              >
                {seriNo ?? '—'}
              </Typography>
              {seriNo && (
                <Tooltip title="Seri numarasını kopyala">
                  <IconButton size="small" onClick={seriKopyala} sx={{ color: 'text.secondary', p: 0.25 }}>
                    <CopyIcon sx={{ fontSize: 13 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          </Box>
          <Tooltip title="Yenile">
            <span>
              <IconButton onClick={() => void getir()} disabled={yukleniyor} size="small" sx={{ color: accent }}>
                {yukleniyor ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
          <IconButton onClick={onClose} size="small" aria-label="Kapat" sx={{ color: 'text.secondary' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Box>

      <DialogContent sx={{ px: 2.5, py: 2, bgcolor: isDark ? '#131313' : '#fafafa' }}>
        {kopyalandi && (
          <Alert severity="success" sx={{ mb: 1.5, py: 0, fontFamily, fontSize: '0.8rem' }}>
            Seri numarası panoya kopyalandı.
          </Alert>
        )}

        {hata && (
          <Alert severity="error" sx={{ mb: 2, fontFamily, fontSize: '0.82rem' }}>
            {hata}
          </Alert>
        )}

        {yukleniyor && !veri && <YuklemeIskeleti />}

        {veri && (
          <>
            <OzetSeridi ozet={veri.ozet} accent={accent} />

            {kutu && <KutuKarti kutu={kutu} accent={accent} kaynagaGit={kaynagaGit} />}

            {veri.ozet.kayitYok ? (
              <BosDurum />
            ) : (
              <Box sx={{ mt: 2.5 }}>
                {gunler.map(grup => (
                  <Box key={grup.gun} sx={{ mb: 1 }}>
                    <Typography
                      sx={{
                        fontFamily,
                        fontSize: '0.7rem',
                        fontWeight: 700,
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        color: 'text.secondary',
                        mb: 1,
                        mt: 1.5,
                      }}
                    >
                      {grup.gun}
                    </Typography>
                    {grup.satirlar.map((satir, sira) => (
                      <ZamanCizelgesiSatiri
                        key={`${satir.zaman}-${satir.baslik}-${sira}`}
                        satir={satir}
                        sonMu={sira === grup.satirlar.length - 1}
                        kaynagaGit={kaynagaGit}
                      />
                    ))}
                  </Box>
                ))}
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.5, borderTop: `1px solid ${theme.palette.divider}` }}>
        <Box sx={{ flex: 1 }}>
          {veri?.ozet.ilkGorulme && (
            <Typography sx={{ fontFamily, fontSize: '0.72rem', color: 'text.secondary' }}>
              İlk kayıt: {tarihSaatMetni(veri.ozet.ilkGorulme)}
            </Typography>
          )}
        </Box>
        <Button size="small" onClick={onClose} sx={{ fontFamily, textTransform: 'none', fontWeight: 600 }}>
          Kapat
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// ————————————————————————— özet —————————————————————————

function OzetSeridi({ ozet, accent }: { ozet: KarekodGecmisiYaniti['ozet']; accent: string }) {
  if (ozet.kayitYok) return null

  const kartlar: { etiket: string; deger: string }[] = [
    { etiket: 'Toplam olay', deger: String(ozet.toplamOlay) },
    { etiket: 'Giriş', deger: String(ozet.girisSayisi) },
    { etiket: 'Çıkış', deger: String(ozet.cikisSayisi) },
    { etiket: 'İTS işlemi', deger: String(ozet.itsSorgusu) },
  ]

  if (ozet.rafOmruGun != null) {
    kartlar.push({ etiket: 'Eczanede kalış', deger: `${ozet.rafOmruGun} gün` })
  }

  return (
    <Stack direction="row" gap={1} flexWrap="wrap">
      {kartlar.map(kart => (
        <Box
          key={kart.etiket}
          sx={{
            flex: '1 1 110px',
            minWidth: 104,
            px: 1.5,
            py: 1,
            borderRadius: 2,
            border: theme => `1px solid ${theme.palette.divider}`,
            bgcolor: theme => alpha(theme.palette.background.paper, 0.9),
          }}
        >
          <Typography sx={{ fontFamily, fontSize: '1.15rem', fontWeight: 700, color: accent, lineHeight: 1.2 }}>
            {kart.deger}
          </Typography>
          <Typography sx={{ fontFamily, fontSize: '0.68rem', color: 'text.secondary' }}>{kart.etiket}</Typography>
        </Box>
      ))}
    </Stack>
  )
}

// ————————————————————————— kutu kartı —————————————————————————

function KutuKarti({
  kutu,
  accent,
  kaynagaGit,
}: {
  kutu: NonNullable<KarekodGecmisiYaniti['kutu']>
  accent: string
  kaynagaGit: (kaynak: HareketKaynagi, yeniSekme: boolean) => void
}) {
  const satirlar: { etiket: string; deger: string; kaynak?: HareketKaynagi }[] = []

  if (kutu.barkod) satirlar.push({ etiket: 'Barkod', deger: kutu.barkod })
  if (kutu.partiNo) satirlar.push({ etiket: 'Parti', deger: kutu.partiNo })
  satirlar.push({ etiket: 'Son kullanma', deger: formatIsoDate(kutu.skt) })

  const geldigiYer = kutu.girisDepoAdi ?? kutu.girisKaynagi
  if (geldigiYer) satirlar.push({ etiket: 'Nereden geldi', deger: geldigiYer })

  // Fatura satırı tıklanabilir: eczacı "bu kutu hangi faturayla girdi" sorusunu görür görmez
  // faturanın kendisini açabilmeli, numarayı bir yere not edip aramak zorunda kalmamalı.
  if (kutu.girisFaturaNo) {
    satirlar.push({
      etiket: 'Fatura',
      deger: kutu.girisFaturaNo,
      kaynak: kutu.girisFaturaId
        ? { tur: 'fatura', etiket: kutu.girisFaturaNo, adres: faturaAdresi(kutu.girisFaturaId) }
        : undefined,
    })
  }

  if (kutu.cikisHedefi) satirlar.push({ etiket: 'Nereye gitti', deger: kutu.cikisHedefi })
  if (kutu.alisFiyati != null) {
    satirlar.push({
      etiket: 'Alış maliyeti',
      deger: new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(kutu.alisFiyati),
    })
  }

  return (
    <Box
      sx={{
        mt: 2,
        px: 2,
        py: 1.5,
        borderRadius: 2,
        border: theme => `1px solid ${theme.palette.divider}`,
        bgcolor: theme => alpha(theme.palette.background.paper, 0.9),
      }}
    >
      <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap" sx={{ mb: 1 }}>
        <Rozet metin={kutu.durumMetni ?? 'Bilinmiyor'} renk={accent} />
        {kutu.sktGecmis && <Rozet metin="Son kullanma tarihi geçmiş" renk="#e5484d" />}
      </Stack>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
          columnGap: 2,
          rowGap: 0.5,
        }}
      >
        {satirlar.map(satir => (
          <Stack key={satir.etiket} direction="row" gap={1} sx={{ minWidth: 0 }}>
            <Typography sx={{ fontFamily, fontSize: '0.74rem', color: 'text.secondary', minWidth: 108 }}>
              {satir.etiket}
            </Typography>
            {satir.kaynak ? (
              <Typography
                component="button"
                type="button"
                onClick={(olay) => kaynagaGit(satir.kaynak!, olay.ctrlKey || olay.metaKey)}
                sx={{
                  fontFamily,
                  fontSize: '0.78rem',
                  fontWeight: 600,
                  wordBreak: 'break-word',
                  textAlign: 'left',
                  border: 'none',
                  background: 'none',
                  p: 0,
                  color: accent,
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  textUnderlineOffset: 2,
                }}
              >
                {satir.deger}
              </Typography>
            ) : satir.etiket === 'Barkod' ? (
              <CopyableBarkod
                value={satir.deger}
                sx={{ fontFamily, fontSize: '0.78rem', fontWeight: 600, wordBreak: 'break-word' }}
              />
            ) : (
              <Typography sx={{ fontFamily, fontSize: '0.78rem', fontWeight: 600, wordBreak: 'break-word' }}>
                {satir.deger}
              </Typography>
            )}
          </Stack>
        ))}
      </Box>
    </Box>
  )
}

function Rozet({ metin, renk }: { metin: string; renk: string }) {
  return (
    <Box
      component="span"
      sx={{
        px: 1,
        py: 0.25,
        borderRadius: 1,
        fontFamily,
        fontSize: '0.7rem',
        fontWeight: 700,
        bgcolor: alpha(renk, 0.16),
        color: renk,
      }}
    >
      {metin}
    </Box>
  )
}

// ————————————————————————— zaman çizelgesi —————————————————————————

const RENKLER: Record<string, string> = {
  basarili: '#30a46c',
  uyari: '#f5a524',
  hata: '#e5484d',
  bilgi: '#5b8def',
}

function satirRengi(renk: string): string {
  return RENKLER[renk] ?? RENKLER.bilgi
}

function satirSimgesi(satir: KarekodZamanCizelgesiSatiri): ReactNode {
  if (satir.kaynak === 'its') return <ItsIcon sx={{ fontSize: 15 }} />
  if (satir.kaynak === 'its-stok') return <ItsStockIcon sx={{ fontSize: 15 }} />
  if (satir.kaynak === 'alis') return <PurchaseIcon sx={{ fontSize: 15 }} />
  if (satir.yon === 'cikis') return <OutIcon sx={{ fontSize: 15 }} />
  if (satir.yon === 'liste') return <TimelineIcon sx={{ fontSize: 15 }} />
  return <InIcon sx={{ fontSize: 15 }} />
}

function ZamanCizelgesiSatiri({
  satir,
  sonMu,
  kaynagaGit,
}: {
  satir: KarekodZamanCizelgesiSatiri
  sonMu: boolean
  kaynagaGit: (kaynak: HareketKaynagi, yeniSekme: boolean) => void
}) {
  const renk = satirRengi(satir.renk)
  const kaynaklar = islemKaynaklari({
    baglanti_turu: satir.baglantiTuru,
    baglanti_id: satir.baglantiId,
    referans: satir.baglantiEtiketi,
  })

  return (
    <Stack direction="row" gap={1.5} sx={{ position: 'relative' }}>
      <Stack alignItems="center" sx={{ width: 28, flexShrink: 0 }}>
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            display: 'grid',
            placeItems: 'center',
            bgcolor: alpha(renk, 0.16),
            color: renk,
            flexShrink: 0,
          }}
        >
          {satirSimgesi(satir)}
        </Box>
        {!sonMu && <Box sx={{ flex: 1, width: '2px', bgcolor: theme => theme.palette.divider, my: 0.5 }} />}
      </Stack>

      <Box sx={{ flex: 1, minWidth: 0, pb: sonMu ? 0.5 : 2 }}>
        <Stack direction="row" alignItems="baseline" gap={1} flexWrap="wrap">
          <Typography sx={{ fontFamily, fontSize: '0.86rem', fontWeight: 700 }}>{satir.baslik}</Typography>
          <Typography sx={{ fontFamily, fontSize: '0.72rem', color: 'text.secondary' }}>
            {satir.yon === 'liste' ? saatSaniyeMetni(satir.zaman) : saatMetni(satir.zaman)}
          </Typography>
          {satir.kullanici && (
            <Typography sx={{ fontFamily, fontSize: '0.72rem', color: 'text.secondary' }}>
              · {satir.kullanici}
            </Typography>
          )}
        </Stack>

        {satir.aciklama && (
          <Typography sx={{ fontFamily, fontSize: '0.78rem', color: 'text.secondary', mt: 0.25 }}>
            {satir.aciklama}
          </Typography>
        )}

        {satir.alanlar.length > 0 && (
          <Box
            sx={{
              mt: 0.75,
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
              columnGap: 2,
              rowGap: 0.25,
            }}
          >
            {satir.alanlar.map((alan, sira) => (
              <Stack key={`${alan.etiket}-${sira}`} direction="row" gap={1} sx={{ minWidth: 0 }}>
                <Typography
                  sx={{ fontFamily, fontSize: '0.72rem', color: 'text.secondary', minWidth: 116, flexShrink: 0 }}
                >
                  {alan.etiket}
                </Typography>
                <Typography sx={{ fontFamily, fontSize: '0.75rem', fontWeight: 600, wordBreak: 'break-word' }}>
                  {alan.deger}
                </Typography>
              </Stack>
            ))}
          </Box>
        )}

        {kaynaklar.length > 0 && (
          <Stack direction="row" gap={0.5} sx={{ mt: 0.6, flexWrap: 'wrap' }}>
            {kaynaklar.map((kaynak) => (
              <Button
                key={`${kaynak.tur}-${kaynak.adres ?? 'panel'}`}
                size="small"
                variant="text"
                startIcon={<KaynakIcon sx={{ fontSize: 15 }} />}
                onClick={(olay) => kaynagaGit(kaynak, olay.ctrlKey || olay.metaKey)}
                sx={{ fontFamily, fontSize: '0.7rem', textTransform: 'none', py: 0.1, minWidth: 0 }}
              >
                {kaynak.etiket}
              </Button>
            ))}
          </Stack>
        )}
      </Box>
    </Stack>
  )
}

// ————————————————————————— boş / yükleniyor —————————————————————————

function BosDurum() {
  return (
    <Stack alignItems="center" gap={1} sx={{ py: 5 }}>
      <EmptyIcon sx={{ fontSize: 38, color: 'text.disabled' }} />
      <Typography sx={{ fontFamily, fontSize: '0.9rem', fontWeight: 600 }}>
        Bu kutu eczanenizden geçmemiş görünüyor.
      </Typography>
      <Typography sx={{ fontFamily, fontSize: '0.8rem', color: 'text.secondary', textAlign: 'center', maxWidth: 380 }}>
        Kutu stoğunuza hiç girmediyse ya da İTS işlemi yapılmadıysa burada kayıt oluşmaz. Doğrulama
        yaptığınızda bu sayfada görünmeye başlar.
      </Typography>
    </Stack>
  )
}

function YuklemeIskeleti() {
  return (
    <Stack gap={1.5}>
      <Stack direction="row" gap={1}>
        {[0, 1, 2, 3].map(sira => (
          <Skeleton key={sira} variant="rounded" height={58} sx={{ flex: 1, borderRadius: 2 }} />
        ))}
      </Stack>
      <Skeleton variant="rounded" height={96} sx={{ borderRadius: 2 }} />
      {[0, 1, 2].map(sira => (
        <Stack key={sira} direction="row" gap={1.5}>
          <Skeleton variant="circular" width={28} height={28} />
          <Skeleton variant="rounded" height={54} sx={{ flex: 1, borderRadius: 2 }} />
        </Stack>
      ))}
    </Stack>
  )
}
